/**
 * Реестр незавершённых редакторов рабочего места (контракты §4, E1.4).
 *
 * Редактор регистрирует себя, пока смонтирован: как узнать, есть ли несохранённые
 * изменения, как сохранить и как отказаться от них. Значения формы остаются у
 * владельца; реестр хранит только обработчики в памяти и ничего не пишет в
 * браузерное хранилище: черновик может содержать секрет.
 */

import { sessionIdentity, useSessionStore } from './session';

export interface DraftHandle {
	/** Есть ли несохранённые изменения сейчас */
	isDirty: () => boolean;
	/** Сохранение силами владельца формы; отказ сервера — отклонённый Promise */
	onSave: () => Promise<void>;
	/** Отказ от изменений */
	onDiscard: () => void;
}

const drafts = new Map<string, DraftHandle>();

export function registerDraft(id: string, handle: DraftHandle): void {
	drafts.set(id, handle);
}

export function unregisterDraft(id: string): void {
	drafts.delete(id);
}

function dirtyDrafts(): DraftHandle[] {
	return [...drafts.values()].filter((draft) => draft.isDirty());
}

export function hasDirtyDrafts(): boolean {
	return dirtyDrafts().length > 0;
}

/**
 * Сохраняет все изменённые черновики. false — хотя бы одно сохранение не удалось:
 * черновик остаётся изменённым, пользователь остаётся на месте.
 */
export async function saveDirtyDrafts(): Promise<boolean> {
	const results = await Promise.allSettled(dirtyDrafts().map((draft) => draft.onSave()));
	return results.every((result) => result.status === 'fulfilled');
}

export function discardDirtyDrafts(): void {
	for (const draft of dirtyDrafts()) {
		draft.onDiscard();
	}
}

export function clearDrafts(): void {
	drafts.clear();
}

/**
 * Выход или вход другого пользователя очищают реестр: черновик прежнего пользователя
 * не достаётся следующему. Повторный вход того же пользователя черновики сохраняет.
 * Возвращает функцию отписки.
 */
export function bindDraftsToSessionIdentity(): () => void {
	let identity = sessionIdentity(useSessionStore.getState().user);
	return useSessionStore.subscribe((state) => {
		const next = sessionIdentity(state.user);
		if (next !== identity) {
			identity = next;
			clearDrafts();
		}
	});
}
